import {useCallback, useEffect, useRef} from 'react';
import {useIsMounted} from '@/hooks/useIsMounted';
import {useDebounce} from '@/hooks/useDebounce';
import {useRosterAwareState} from '@/hooks/useRosterAwareState';
import {FilterInvalidate} from '@common/FilterComponent';
import {QueryFnCallback} from '@/hooks/useFilterQuery';

type Options = {
  invalidate: FilterInvalidate;
  queryFnCallback: QueryFnCallback;
  canInvalidate?: ()=>boolean;
}

export function useSearchQuery({ invalidate, queryFnCallback, canInvalidate }: Options) {
  const [searchTerm, setSearchTerm] = useRosterAwareState('');
  const debouncedSearchTerm = useDebounce(searchTerm);

  const isMounted = useIsMounted();
  const anyFilterIsActiveRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const anyFilterIsActive = queryFnCallback(!!debouncedSearchTerm);

    if (!anyFilterIsActive && (!canInvalidate || canInvalidate())){
      anyFilterIsActiveRef.current = anyFilterIsActive;
      invalidate();
    }
  }, [debouncedSearchTerm]);

  const searchFunction = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm);
  }, [setSearchTerm]);

  const queryFnSearch = () => {
    const anyFilterIsActive = anyFilterIsActiveRef.current ?? queryFnCallback(!!debouncedSearchTerm);
    anyFilterIsActiveRef.current = null;

    if (anyFilterIsActive) {
      setSearchTerm('');
    }

    return !anyFilterIsActive ? debouncedSearchTerm : '';
  };

  return {
    searchTerm,
    searchFunction,
    queryFnSearch,
  };
}
